import React from "react";
import dayjs from "dayjs";
import { t, Trans } from "@lingui/macro";
import { getColor } from "../../../../utils";
import { Alert } from "../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { ButtonLink } from "../../../../UI/ButtonLink/ButtonLink";
import { DotsVerticalIcon, MinusSmIcon } from "@heroicons/react/outline";
import { numberCurrencyFormat } from "../../../../../constants/convertToLocalCurrency";
import { selectSalesArchiveTableData } from "../../../../../model/dashboardSalesArchive/selectors";
import { dSalesArchiveSetDetailItemAC } from "../../../../../model/dashboardSalesArchive/actionCreators";
import {
  SALESTATUS,
  getSalesTransStatus,
} from "../../../../../constants/salesStatus";
import {
  getIconPaymentType,
  getStringPaymentType,
} from "../../../../../constants/paymentType";

export type typeTableHeader = {
  variant?: string;
};

export const TableHeader: React.FC<typeTableHeader> = () => {
  return (
    <>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Sale date</Trans> / <Trans>Receipt number</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Store</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Employee</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Total amount</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Credit</Trans> / <Trans>Prepayment</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Payment method</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans id="total_sales">Total</Trans>
      </th>
      <th
        scope="col"
        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
      >
        <Trans>Refunds</Trans>
      </th>
      <th
        scope="col"
        className="w-1 px-4 py-3 text-xs font-medium uppercase tracking-wider text-center"
      />
    </>
  );
};

export type typeAction = {
  title: React.JSX.Element;
  disableSwitcher?: boolean;
  action: (id: string) => void;
};

export type typeTableBody = {
  variant?: string;
  rowsActions: typeAction[];
  setOpenPopupDetails: React.Dispatch<React.SetStateAction<boolean>>;
};

export const TableBody: React.FC<typeTableBody> = ({
  variant,
  rowsActions,
  setOpenPopupDetails,
}) => {
  const dispatch = useDispatch();
  const color = getColor(variant);
  const tableData = useSelector(selectSalesArchiveTableData);

  let component;

  const onCellClickHandler = (
    e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    id: string,
  ) => {
    setOpenPopupDetails(true);

    dispatch(dSalesArchiveSetDetailItemAC(id));
  };

  if (tableData.length) {
    component = tableData.map((item, index) => {
      let date = "-";

      if (item.date) {
        if (dayjs(item.date).isValid()) {
          date = dayjs(item.date).format("YYYY-MM-DD, HH:mm:ss");
        } else {
          date = item.date.substring(0, 19).replace("T", " , ");
        }
      }

      return (
        <tr
          key={item.id}
          className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-${color}-50`}
        >
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {date} / {item.receiptNumber ? item.receiptNumber : "-"}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.store?.name || (
              <div className="flex">
                <MinusSmIcon className="h-5 w-5 text-gray-400" />
              </div>
            )}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.userName || (
              <div className="flex">
                <MinusSmIcon className="h-5 w-5 text-gray-400" />
              </div>
            )}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.amount ? (
              numberCurrencyFormat(item.amount)
            ) : (
              <div className="flex">
                <MinusSmIcon className="h-5 w-5 text-gray-400" />
              </div>
            )}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.status ? (
              item.status === SALESTATUS.SALE ? (
                ""
              ) : (
                getSalesTransStatus(item.status)
              )
            ) : (
              <div className="flex">
                <MinusSmIcon className="h-5 w-5 text-gray-400" />
              </div>
            )}
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="flex flex-col justify-center divide-y">
              {item.payments.length ? (
                item.payments.map((it, index) => {
                  return (
                    <div key={index + item.id}>
                      <p
                        className=" flex gap-1"
                        title={getStringPaymentType(it.paymentType)}
                      >
                        <span
                          className="text-gray-500 flex gap-1"
                          title={t`${getStringPaymentType(it.paymentType)}`}
                        >
                          {getIconPaymentType(it.paymentType)}
                          {it.paymentInfo?.paymentSystemName
                            ? `  ${it.paymentInfo.paymentSystemName}`
                            : ""}
                        </span>
                        <span> : </span>
                        <span className="text-gray-500">
                          {numberCurrencyFormat(it.paymentAmount)}
                        </span>
                      </p>
                    </div>
                  );
                })
              ) : (
                <div className="flex">
                  <MinusSmIcon className="h-5 w-5 text-gray-400" />
                </div>
              )}
            </div>
          </td>
          <td
            className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            <div className="">
              {item.payments.length
                ? item.payments
                    .reduce((sum, item) => sum + item.paymentAmount, 0)
                    .toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })
                : "-"}
            </div>
          </td>
          <td
            className="px-3 py-1sm:px-4 sm:py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
            onClick={(e) => onCellClickHandler(e, item.id)}
          >
            {item.refunded ? (
              <div className="flex">
                <Trans>Yes</Trans>
              </div>
            ) : (
              ""
            )}
          </td>

          <td className="whitespace-nowrap text-sm text-gray-500 pr-2">
            <div className="relative flex ">
              <div className="row-action-block">
                <DotsVerticalIcon className="row-action-list-open-button" />

                <div className="row-action-list">
                  <div className="flex gap-x-4">
                    {rowsActions.map((actionItem, ActionIndex) => (
                      <ButtonLink
                        type="button"
                        key={ActionIndex}
                        label={actionItem.title}
                        onClick={() => actionItem.action(item.id)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      );
    });
  } else {
    component = (
      <tr>
        <td className="p-2" colSpan={9}>
          <Alert
            type="info"
            className="w-full"
            title={<Trans>The Sales list is empty. Check filters.</Trans>}
          />
        </td>
      </tr>
    );
  }

  return <>{component}</>;
};
